/**
 * WMS orders facade: delegates to picqer-orders or channeldock-orders based on wms_provider.
 * Same methods as PicqerOrdersService so the WMS orders page can use one API.
 */
import { supabase } from '../lib/supabase';
import type { PicqerOrderFromApi, PicqerProductFromApi } from './picqerOrdersService';

export type WmsProvider = 'picqer' | 'channeldock';

interface OrdersListResponse {
  success: boolean;
  data?: PicqerOrderFromApi[];
  error?: string;
}

interface ProductsListResponse {
  success: boolean;
  data?: PicqerProductFromApi[];
  error?: string;
}

interface CreateOrderResponse {
  success: boolean;
  data?: { idorder: number; orderid: string };
  error?: string;
}

interface ProcessOrderResponse {
  success: boolean;
  data?: { idorder: number };
  error?: string;
}

function getFunctionName(provider: WmsProvider): string {
  return provider === 'channeldock' ? 'channeldock-orders' : 'picqer-orders';
}

export const WmsOrdersService = {
  async listOrders(companyId: number, wmsProvider: WmsProvider): Promise<OrdersListResponse> {
    const fn = getFunctionName(wmsProvider);
    const { data, error } = await supabase.functions.invoke(fn, {
      body: { action: 'list_orders', company_id: companyId },
    });
    if (error) {
      const msg = (error as { message?: string }).message ?? 'Failed to list orders';
      return { success: false, error: msg };
    }
    const out = data as OrdersListResponse;
    return out?.success ? out : { success: false, data: [], error: out?.error ?? 'Unknown error' };
  },

  async listProducts(companyId: number, wmsProvider: WmsProvider): Promise<ProductsListResponse> {
    const fn = getFunctionName(wmsProvider);
    const { data, error } = await supabase.functions.invoke(fn, {
      body: { action: 'list_products', company_id: companyId },
    });
    if (error) {
      const msg = (error as { message?: string }).message ?? 'Failed to list products';
      return { success: false, error: msg };
    }
    const out = data as ProductsListResponse;
    return out?.success ? out : { success: false, data: [], error: out?.error ?? 'Unknown error' };
  },

  async createOrder(
    companyId: number,
    wmsProvider: WmsProvider,
    payload: Record<string, unknown>
  ): Promise<CreateOrderResponse> {
    const fn = getFunctionName(wmsProvider);
    const { data, error } = await supabase.functions.invoke(fn, {
      body: { action: 'create_order', company_id: companyId, payload },
    });
    if (error) {
      return { success: false, error: (error as { message?: string }).message ?? 'Failed to create order' };
    }
    const out = data as CreateOrderResponse;
    return out ?? { success: false, error: 'No response' };
  },

  /** Only used for Picqer; ChannelDock has no separate process step. */
  async processOrder(
    companyId: number,
    wmsProvider: WmsProvider,
    idorder: number
  ): Promise<ProcessOrderResponse> {
    if (wmsProvider === 'channeldock') {
      return { success: true, data: { idorder } };
    }
    const { data, error } = await supabase.functions.invoke('picqer-orders', {
      body: { action: 'process_order', company_id: companyId, idorder },
    });
    if (error) {
      return { success: false, error: (error as { message?: string }).message ?? 'Failed to process order' };
    }
    const out = data as ProcessOrderResponse;
    return out ?? { success: false, error: 'No response' };
  },
};
